import React from 'react';
import { Gauge, Crosshair, CalendarCheck } from 'lucide-react';

/**
 * WhatYoullDiscover
 *
 * Three-bullet teaser block that sits directly below the hero on the
 * marketing homepage. Its only job is to sell the Mindset Quiz — set
 * expectations for what the athlete (or parent / coach) will get when
 * they take it, without giving away the actual diagnosis. Copy is
 * intentionally outcome-focused, not feature-focused.
 */
const BULLETS = [
  {
    icon: Gauge,
    title: 'Your Mental Edge Score',
    body: 'A clear read on where you stand across the 6 dimensions of mental performance — composure, confidence, focus, and more.',
  },
  {
    icon: Crosshair,
    title: 'The #1 mindset gap',
    body: 'The single pattern quietly costing you reps, minutes, or wins — named, explained, and ranked against the rest of your profile.',
  },
  {
    icon: CalendarCheck,
    title: 'A 7-day action plan',
    body: 'A custom protocol you can start tonight. Same framework used by athletes on a 2025 NJ State Championship roster.',
  },
];

export const WhatYoullDiscover = () => {
  return (
    <section
      id="what-youll-discover"
      className="relative bg-[#0a0d0e] py-14 sm:py-18"
      data-testid="what-youll-discover"
    >
      <div className="max-w-[1120px] mx-auto px-5 sm:px-6">
        <div className="text-center mb-10 sm:mb-12">
          <div className="font-mono-accent uppercase tracking-[0.22em] text-[10.5px] font-bold text-[#4FBCC7] mb-3">
            What you&rsquo;ll get from the quiz
          </div>
          <h2 className="font-heading text-[1.8rem] sm:text-[2.3rem] font-bold tracking-tight text-white leading-[1.15] max-w-[34ch] mx-auto">
            Stop guessing. Start training the mental side of your game.
          </h2>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 sm:gap-5">
          {BULLETS.map((b) => {
            const BIcon = b.icon;
            return (
              <div
                key={b.title}
                className="rounded-2xl bg-white/[0.04] border border-white/10 p-5 sm:p-6 hover:border-[#4FBCC7]/40 hover:bg-white/[0.06] transition-colors"
                data-testid={`wyd-bullet-${b.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
              >
                <div className="w-11 h-11 rounded-xl bg-[#004C54]/25 border border-[#4FBCC7]/30 flex items-center justify-center mb-3">
                  <BIcon className="h-5 w-5 text-[#4FBCC7]" />
                </div>
                <h3 className="font-heading text-[1.05rem] font-bold tracking-tight text-white mb-1.5">
                  {b.title}
                </h3>
                <p className="text-[13.5px] text-white/65 leading-relaxed">
                  {b.body}
                </p>
              </div>
            );
          })}
        </div>

        <div className="mt-9 sm:mt-10 text-center">
          <a
            href="/quiz"
            data-testid="wyd-quiz-cta"
            className="inline-flex items-center gap-2 px-7 h-12 rounded-xl bg-[#004C54] hover:bg-[#003a40] text-white font-bold text-[14.5px] shadow-lg shadow-[#004C54]/40 transition-colors"
          >
            Take the Free Mindset Quiz
            <span aria-hidden="true">→</span>
          </a>
          <p className="mt-3 text-[12px] text-white/50">
            Free · 2 minutes · No credit card · Instant results
          </p>
        </div>
      </div>
    </section>
  );
};

export default WhatYoullDiscover;
